/**
 * Created by Ning Jiang on 9/3/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {GameMode, IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {ReelStopPresentationController} from "@nolimitcity/slot-game/bin/core/reelstoppresentation/ReelStopPresentationController";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {ISpinConfig} from "@nolimitcity/slot-game/bin/core/spin/SpinController";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {TimelineLite} from "gsap";
import {IceIceYetiSoundConfig} from "../../IceIceYetiSoundConfig";
import {IceIceYetiParsedGameData} from "../../server/data/IceIceYetiParsedGameData";

export class SpinSoundController extends ReelStopPresentationController {

    private _nextMode:string;
    private _currentMode:string;
    private _currentSpinLoop:number = -1;
    private _spinLoopTimeline:TimelineLite;
    private _startWindLoopTL:TimelineLite;
    private _stopWindLoopTL:TimelineLite;
    private _isRespinPlaying:boolean = false;
    private _nearWinReels:boolean[];
    private _isFirstRespin:boolean = false;
    private _isLastRespin:boolean = false;
    private _isNearWinPlaying:boolean = false;
    private _respinCounter:number = -1;
    private _respinSpinSounds:string[] = [
        IceIceYetiSoundConfig.instance.RESPIN_LEVEL_01,
        IceIceYetiSoundConfig.instance.RESPIN_LEVEL_02,
        IceIceYetiSoundConfig.instance.RESPIN_LEVEL_03,
        IceIceYetiSoundConfig.instance.RESPIN_LEVEL_04,
        IceIceYetiSoundConfig.instance.RESPIN_LEVEL_05
    ];

    constructor(index:number) {
        super(index, "SpinSoundController");
        this.createWindLoopsTL();
    }

    private createWindLoopsTL():void {
        this._startWindLoopTL = new TimelineLite({paused : true});
        this._startWindLoopTL.add(() => SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.WIND_LOOP, -1), "+=1");
        this._startWindLoopTL.add(() => SlotGame.sound.fadeEffect(IceIceYetiSoundConfig.instance.WIND_LOOP, 1, 3000), "+=1");

        this._stopWindLoopTL = new TimelineLite({paused : true});
        this._stopWindLoopTL.add(() => SlotGame.sound.fadeEffect(IceIceYetiSoundConfig.instance.WIND_LOOP, 0, 3000));
        this._stopWindLoopTL.add(() => SlotGame.sound.stopEffect(IceIceYetiSoundConfig.instance.WIND_LOOP), "+=3");
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ScreenEvent.GAME_START, (event:GameEvent) => this.onGameStart());
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, (event:GameEvent) => this.onGameReady());
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.parseFeatureGameData(event.params[0]));
        EventHandler.addEventListener(this, SpinEvent.START, (event:GameEvent) => this.onSpinStart(event.params[0]));
        EventHandler.addEventListener(this, GameModeEvent.CHANGE_MODE, (event:GameEvent) => this.onChangeGameMode(event.params[0]));
    }

    private onGameStart():void{
        SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.AMBIENT, -1);
    }

    private onGameReady():void {
        //Start wind loop if restore into respin
        if(this._nextMode == GameMode.RESPIN) {
            this._startWindLoopTL.restart();
        }
    }

    protected parseFeatureGameData(data:IceIceYetiParsedGameData):void {
        this._nextMode = data.nextMode;
        this._nearWinReels = data.nearWinReels != null ? data.nearWinReels : ArrayHelper.initArrayWithValues<boolean>(GameConfig.instance.REELS_NUM, (index:number) => {return false});
        this._isLastRespin = data.mode === GameMode.RESPIN && data.nextMode === GameMode.NORMAL;
        this._isFirstRespin = data.mode === GameMode.NORMAL && data.nextMode === GameMode.RESPIN;
    }

    protected onSpinStart(config:ISpinConfig):void {
        if(this._spinLoopTimeline && this._spinLoopTimeline.isActive()) {
            this._spinLoopTimeline.pause();
            this._spinLoopTimeline.kill();
            SlotGame.sound.fadeAmbience(0, 0);
        }

        if(this._nextMode === GameMode.RESPIN) {
            this.playRespinLoop();
            this._respinCounter++;
            if(this._respinCounter > 4) {
                this._respinCounter = 4;
            }
            SlotGame.sound.playEffect(this._respinSpinSounds[this._respinCounter]);
        } else {
            this._respinCounter = -1;
            const timeline:TimelineLite = new TimelineLite();
            timeline.add(() => this.playSpinLoop());
            timeline.add(() => this.stopSpinLoop(100), 7);
            this._spinLoopTimeline = timeline;
            this._spinLoopTimeline.play();
        }
    }

    private playSpinLoop():void {
        this._currentSpinLoop = this.getRandomSpinLoopIndex();
        SlotGame.sound.playAmbience(IceIceYetiSoundConfig.instance.SPIN_LOOPS[this._currentSpinLoop], 0);
        SlotGame.sound.fadeAmbience(1, 0);
    }

    private getRandomSpinLoopIndex():number {
        const index:number = Math.floor(Math.random() * IceIceYetiSoundConfig.instance.SPIN_LOOPS.length);
        return index === this._currentSpinLoop ? this.getRandomSpinLoopIndex() : index;
    }

    private stopSpinLoop(duration:number):void {
        SlotGame.sound.fadeAmbience(0, duration);
    }

    public playRespinLoop():void {
        if(this._isRespinPlaying) {
            return;
        }
        this._isRespinPlaying = true;
        SlotGame.sound.playAmbience(IceIceYetiSoundConfig.instance.RESPIN_BACKGROUND, 0);
        SlotGame.sound.fadeAmbience(1, 0);
    }

    public stopRespinLoop():void {
        if(!this._isRespinPlaying) {
            return;
        }

        this._isRespinPlaying = false;
        this.stopSpinLoop(100);
    }

    public onReelBounce(data:IReelStoppedData):void {
        // #100 Fade out spin melody if nearwin
        if(this._nearWinReels[data.reelId + 1]) {
            if(!this._isNearWinPlaying) {
                this.stopSpinLoop(500);
                this._isNearWinPlaying = true;
            }
        } else if(this._isNearWinPlaying && data.reelId < 4) {
            this._isNearWinPlaying = false;
            // TODO: fade in spin sound again?
        }

        if(data.reelId === 4) {
            if((data.quickStop || data.fastSpin) && !this._isRespinPlaying) {
                this.stopSpinLoop(100); // only stop normal spin loop.
                if(!this._nearWinReels[4]) {
                    this.playSpinEndSound();
                }
            }

            if(this._respinCounter > -1) {
                SlotGame.sound.stopEffect(this._respinSpinSounds[this._respinCounter]);
            }

            // #99
            if(this._isNearWinPlaying && this._nextMode != GameMode.RESPIN) {
                SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.NEAR_WIN_FAIL);
            }

            if(this._isLastRespin) {
                this.stopRespinLoop();
                SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.AMBIENT, -1);
                // #103
                if(GameSetting.isSoundLoopOn) {
                    SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.RESPIN_ENDS);
                }
                //Stop wind loop
                this._stopWindLoopTL.restart();
            }

            if(this._isFirstRespin) {
                //Start wind loop
                this._startWindLoopTL.restart();
            }

            this._isNearWinPlaying = false;
        }

        this.dispatchCompleteEvent(data.reelId);
    }

    private playSpinEndSound():void {
        if(GameSetting.isSoundLoopOn) {
            SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.SPIN_ENDS);
        }
    }

    private onChangeGameMode(data:IGameModeChangedData):void {
        if(data.newMode === this._currentMode) {
            return;
        }
        this._currentMode = data.newMode;
        if(this._currentMode === GameMode.RESPIN) {
            SlotGame.sound.stopEffect(IceIceYetiSoundConfig.instance.AMBIENT);
        } else {
        }
    }
}